import { apiClient } from "../../constants.ts";
import {
  Button,
  Collapsible,
  customConfirm,
  inputClass,
  PageSpinner,
  TokenDisplay,
} from "./components.tsx";
import { transact, tx, useAuth, useQuery } from "./db.ts";

import { useEffect, useState } from "preact/hooks";
import { toast } from "react-hot-toast";

type WhatsappNumber = {
  id: string;
  phoneNumberId: string;
  displayNumber: string;
  codeVerificationStatus?: string;
  isDisabled?: boolean;
};

const VerifyNumberRow = (
  { botId, number, authToken }: {
    botId: string;
    number: WhatsappNumber;
    authToken: string;
  },
) => {
  const [code, setCode] = useState("");
  const verified = number.codeVerificationStatus === "VERIFIED";
  return (
    <div className="flex flex-col md:flex-row md:items-center gap-2 border-b py-2">
      <div className="flex items-start gap-2.5 flex-1">
        <input
          type="checkbox"
          checked={!number.isDisabled}
          onChange={(e) => {
            const checked = (e.target as HTMLInputElement).checked;
            toast.promise(
              transact([
                tx.whatsappBusinessNumbers[number.id].update({
                  isDisabled: !checked,
                }),
              ]),
              {
                loading: checked ? "Enabling line..." : "Disabling line...",
                success: checked ? "Line enabled" : "Line disabled",
                error: "Failed to update connection state",
              },
            );
          }}
          className="h-4 w-4 mt-1 rounded border-gray-300 text-accent focus:ring-accent cursor-pointer"
          title={number.isDisabled
            ? "Line is currently disabled. Check to enable."
            : "Line is currently enabled. Uncheck to disable."}
        />
        <div>
          <div
            className={`font-medium ${
              number.isDisabled ? "line-through opacity-60" : ""
            }`}
          >
            {number.displayNumber}
          </div>
          <div className="text-xs opacity-70">
            ID: {number.phoneNumberId} · Status:{" "}
            {number.codeVerificationStatus ?? "UNKNOWN"}
          </div>
        </div>
      </div>
      {verified
        ? (
          <div className="flex gap-2 items-center flex-wrap">
            <span className="text-success text-sm">Verified</span>
            <Button
              onClick={() => {
                toast.promise(
                  apiClient({
                    token: authToken,
                    endpoint: "register-whatsapp-number",
                    payload: { botId, phoneNumberId: number.phoneNumberId },
                  }),
                  {
                    loading: `Re-registering ${number.displayNumber}…`,
                    success: (res) => {
                      if (res.success) {
                        return res.alreadyRegistered
                          ? `${number.displayNumber} already registered on Cloud API`
                          : `${number.displayNumber} registered on Cloud API`;
                      }
                      throw new Error(res.error);
                    },
                    error: (err) => `Failed to register: ${err.message}`,
                  },
                );
              }}
            >
              Re-register
            </Button>
          </div>
        )
        : (
          <div className="flex gap-2 items-center flex-wrap">
            <Button
              onClick={() => {
                toast.promise(
                  apiClient({
                    token: authToken,
                    endpoint: "request-whatsapp-code",
                    payload: { botId, phoneNumberId: number.phoneNumberId },
                  }),
                  {
                    loading: `Sending SMS code to ${number.displayNumber}…`,
                    success: (res) => {
                      if (res.success) {
                        return `SMS code sent to ${number.displayNumber}`;
                      }
                      throw new Error(res.error);
                    },
                    error: (err) => `Failed to send code: ${err.message}`,
                  },
                );
              }}
            >
              Send SMS code
            </Button>
            <input
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
              className={inputClass}
              placeholder="SMS code"
            />
            <Button
              disabled={!code.trim()}
              onClick={() => {
                toast.promise(
                  apiClient({
                    token: authToken,
                    endpoint: "verify-whatsapp-number",
                    payload: {
                      botId,
                      code: code.trim(),
                      phoneNumberId: number.phoneNumberId,
                    },
                  }),
                  {
                    loading: `Verifying ${number.displayNumber}...`,
                    success: (res) => {
                      if (res.success) {
                        setCode("");
                        return `Verified ${number.displayNumber}`;
                      }
                      throw new Error(res.error);
                    },
                    error: (err) => `Failed to verify: ${err.message}`,
                  },
                );
              }}
            >
              Verify
            </Button>
            <Button
              onClick={() => {
                toast.promise(
                  apiClient({
                    token: authToken,
                    endpoint: "register-whatsapp-number",
                    payload: { botId, phoneNumberId: number.phoneNumberId },
                  }),
                  {
                    loading: `Registering ${number.displayNumber}…`,
                    success: (res) => {
                      if (res.success) {
                        return `${number.displayNumber} registered on Cloud API`;
                      }
                      throw new Error(res.error);
                    },
                    error: (err) => `Failed to register: ${err.message}`,
                  },
                );
              }}
            >
              Register only
            </Button>
          </div>
        )}
    </div>
  );
};

const NumbersList = (
  { botId, authToken }: { botId: string; authToken: string },
) => {
  const { data, error, isLoading } = useQuery({
    bots: {
      $: { where: { id: botId } },
      whatsappBusinessAccounts: { numbers: {} },
    },
  });
  if (isLoading) {
    return <div className="text-sm opacity-70">Loading numbers…</div>;
  }
  if (error) return <div>Error: {error.message}</div>;
  const accounts = data?.bots[0]?.whatsappBusinessAccounts ?? [];
  const numbers: WhatsappNumber[] = accounts.flatMap((
    a: { numbers?: WhatsappNumber[] },
  ) => a.numbers ?? []);
  if (numbers.length === 0) {
    return (
      <div className="text-sm opacity-70">
        No numbers synced yet. Click "Sync Numbers" above.
      </div>
    );
  }
  return (
    <div className="flex flex-col">
      <div className="text-sm font-medium mb-1">Phone numbers</div>
      {numbers.map((n) => (
        <VerifyNumberRow
          key={n.id}
          botId={botId}
          number={n}
          authToken={authToken}
        />
      ))}
    </div>
  );
};

export const WhatsappForBusinessConfig = ({ id }: { id: string }) => {
  const [newToken, setNewToken] = useState("");
  const [appSecret, setAppSecret] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const token = useAuth().user?.refresh_token;
  const { data, error, isLoading } = useQuery({
    bots: {
      $: { where: { id } },
      whatsappBusinessAccounts: { numbers: {} },
    },
  });
  const bot = data?.bots?.[0];
  useEffect(() => {
    if (bot?.whatsappForBusinessWabaId) {
      setWabaId(bot.whatsappForBusinessWabaId);
    }
    if (bot?.whatsappForBusinessAppSecret) {
      setAppSecret(bot.whatsappForBusinessAppSecret);
    }
  }, [bot?.whatsappForBusinessWabaId, bot?.whatsappForBusinessAppSecret]);
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  if (!bot) return <div>No bot found</div>;
  const { whatsappForBusinessToken } = bot;
  const accounts = bot.whatsappBusinessAccounts ?? [];
  const syncedNumbers: WhatsappNumber[] = accounts.flatMap((
    a: { numbers?: WhatsappNumber[] },
  ) => a.numbers ?? []);
  return (
    <Collapsible title="WhatsApp for Business">
      <a href="/blog/whatsapp-for-business-setup">
        Step by step instructions
      </a>
      <div className="flex flex-col gap-2">
        {!whatsappForBusinessToken
          ? (
            <div className="mb-4">
              <label>System User Token</label>
              <input
                value={newToken}
                onChange={(e) => setNewToken(e.currentTarget.value)}
                className={inputClass}
              />
              <label className="mt-3 block">App Secret</label>
              <input
                value={appSecret}
                onChange={(e) => setAppSecret(e.currentTarget.value)}
                className={inputClass}
                placeholder="Found in App Settings > Basic > App Secret"
              />
              <label className="mt-3 block">
                WhatsApp Business Account ID
              </label>
              <input
                value={wabaId}
                onChange={(e) => setWabaId(e.currentTarget.value)}
                className={inputClass}
                placeholder="Found in Meta Business Suite URL (asset_id parameter). Required."
              />
              <label className="mt-3 block">
                Phone Number ID (optional)
              </label>
              <input
                value={phoneNumberId}
                onChange={(e) => setPhoneNumberId(e.currentTarget.value)}
                className={inputClass}
                placeholder="Optional. Found in Meta Developer Dashboard > WhatsApp > API Setup"
              />
              {syncedNumbers.length > 0 && (
                <div className="mt-3 p-3 bg-base-200 rounded-lg text-sm">
                  <div className="font-medium mb-1.5 opacity-80">
                    Detected phone numbers in account:
                  </div>
                  <div className="flex flex-col gap-1.5">
                    {syncedNumbers.map((n) => (
                      <div
                        key={n.id}
                        onClick={() => setPhoneNumberId(n.phoneNumberId)}
                        className={`cursor-pointer p-2 rounded border transition-colors flex items-center justify-between ${
                          phoneNumberId === n.phoneNumberId
                            ? "border-accent bg-accent/10"
                            : "border-base-300 hover:border-accent/50"
                        }`}
                        title="Click to select this phone number"
                      >
                        <div>
                          <span className="font-mono font-medium">
                            {n.displayNumber}
                          </span>
                          <span className="opacity-70 text-xs ml-2">
                            (ID: {n.phoneNumberId})
                          </span>
                        </div>
                        <span
                          className={`text-xs px-2 py-0.5 rounded font-medium ${
                            n.codeVerificationStatus === "VERIFIED"
                              ? "bg-success/20 text-success"
                              : "bg-warning/20 text-warning"
                          }`}
                        >
                          {n.codeVerificationStatus ?? "UNKNOWN"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Button
                onClick={() => {
                  if (!token) return;
                  toast.promise(
                    apiClient({
                      token,
                      endpoint: "setup-whatsapp-for-business",
                      payload: {
                        botId: bot.id,
                        token: newToken,
                        appSecret,
                        wabaId: wabaId.trim(),
                        ...(phoneNumberId.trim()
                          ? { phoneNumberId: phoneNumberId.trim() }
                          : {}),
                      },
                    }),
                    {
                      loading:
                        "Connecting to WhatsApp for Business (this may take a moment)...",
                      success: (res) => {
                        if (res.success) {
                          setNewToken("");
                          setAppSecret("");
                          setWabaId("");
                          setPhoneNumberId("");
                          if (res.codeRequested) {
                            return `Connected! Verification codes sent via SMS.`;
                          }
                          return "Connected!";
                        }
                        throw new Error(res.error);
                      },
                      error: (err) => `Failed to connect: ${err.message}`,
                    },
                  );
                }}
                disabled={!newToken || !appSecret || !wabaId.trim()}
                className="w-full md:w-auto mt-2"
              >
                Connect
              </Button>
            </div>
          )
          : (
            <>
              {whatsappForBusinessToken && (
                <TokenDisplay
                  label="Token"
                  token={whatsappForBusinessToken}
                />
              )}
              <label className="mt-2 block">
                WhatsApp Business Account ID
              </label>
              <input
                value={wabaId}
                onChange={(e) => setWabaId(e.currentTarget.value)}
                className={inputClass}
                placeholder="Required to sync numbers"
              />
              <label className="mt-3 block">App Secret</label>
              <input
                value={appSecret}
                onChange={(e) => setAppSecret(e.currentTarget.value)}
                className={inputClass}
                placeholder="Found in App Settings > Basic > App Secret. Required."
              />
              {whatsappForBusinessToken && (
                <Button
                  onClick={() => {
                    if (!token) return;
                    toast.promise(
                      apiClient({
                        token,
                        endpoint: "setup-whatsapp-for-business",
                        payload: {
                          botId: bot.id,
                          token: whatsappForBusinessToken,
                          appSecret,
                          wabaId: wabaId.trim(),
                        },
                      }),
                      {
                        loading: "Syncing numbers...",
                        success: (res) => {
                          if (res.success) return "Numbers synced!";
                          throw new Error(res.error);
                        },
                        error: (err) => `Failed to sync: ${err.message}`,
                      },
                    );
                  }}
                  disabled={!wabaId.trim() || !appSecret}
                  className="w-fit mt-2"
                >
                  Sync Numbers
                </Button>
              )}
              {token && <NumbersList botId={bot.id} authToken={token} />}
              <Button
                className="w-fit mt-2 bg-danger hover:bg-danger-hover"
                onClick={async () => {
                  if (
                    !await customConfirm(
                      "Are you sure you want to disconnect WhatsApp for Business? You'll need to set it up again to reconnect.",
                    )
                  ) return;
                  toast.promise(
                    transact([
                      tx.bots[id].update({
                        whatsappForBusinessToken: null,
                        whatsappForBusinessVerifyCode: null,
                        whatsappForBusinessWabaId: null,
                      }),
                    ]),
                    {
                      loading: "Disconnecting...",
                      success: "WhatsApp for Business disconnected",
                      error: "Failed to disconnect",
                    },
                  );
                }}
              >
                Disconnect
              </Button>
            </>
          )}
      </div>
    </Collapsible>
  );
};
